// modules/ItemSystem.js
// 道具系统模块
import InventoryHelper from './InventoryHelper.js';
import ValidationHelper from './ValidationHelper.js';

export default class ItemSystem {
    /**
     * 使用道具
     * @param {Object} item - 道具对象
     * @param {Object} player - 玩家对象
     * @returns {Object} 使用结果 {success: boolean, message: string, effect?: Object}
     */
    static useItem(item, player) {
        // 使用ValidationHelper验证数据
        const itemValidation = ValidationHelper.validateGameData(item, '道具');
        const playerValidation = ValidationHelper.validateGameData(player, '玩家');
        
        if (!itemValidation.valid || !playerValidation.valid) {
            return {
                success: false,
                message: itemValidation.message || playerValidation.message || '道具或玩家数据无效'
            };
        }
        
        // 检查道具类型
        switch(item.type) {
            case 'consumable':
                return this.useConsumable(item, player);
            case 'equipment':
                return {
                    success: false,
                    message: '装备需要在装备菜单中穿戴'
                };
            default:
                return {
                    success: false,
                    message: '该道具无法使用'
                };
        }
    }
    
    /**
     * 使用消耗品
     * @param {Object} item - 消耗品道具
     * @param {Object} player - 玩家对象
     * @returns {Object} 使用结果
     */
    static useConsumable(item, player) {
        const result = {
            success: false,
            message: '',
            effect: {}
        };
        
        // 恢复生命值
        if (item.healAmount) {
            const oldHp = player.hp;
            player.hp = Math.min(player.maxHp, player.hp + item.healAmount);
            const healed = player.hp - oldHp;
            
            result.success = true;
            result.effect.healed = healed;
            result.message = `恢复了 ${healed} 点生命值`;
            
            // 如果已经满血
            if (healed === 0) {
                result.message = '生命值已满，无需使用';
                result.success = false;
            }
        }
        // 修复战车
        else if (item.repairAmount && player.vehicle) {
            if (!player.vehicle.currentChassisHp) {
                player.vehicle.currentChassisHp = player.vehicle.chassisHp || 0;
            }
            
            const oldHp = player.vehicle.currentChassisHp;
            player.vehicle.currentChassisHp = Math.min(
                player.vehicle.chassisHp,
                player.vehicle.currentChassisHp + item.repairAmount
            );
            const repaired = player.vehicle.currentChassisHp - oldHp;
            
            result.success = true;
            result.effect.repaired = repaired;
            result.message = `战车修复了 ${repaired} 点耐久`;
            
            // 如果已经满耐久
            if (repaired === 0) {
                result.message = '战车耐久已满，无需使用';
                result.success = false;
            }
        }
        // 补充燃料（如果有燃料系统）
        else if (item.fuelAmount && player.vehicle) {
            // 这里可以扩展燃料系统
            result.success = true;
            result.message = `补充了 ${item.fuelAmount} 点燃料`;
        }
        // 伤害道具（如手榴弹、火箭弹）- 只能在战斗中使用
        else if (item.damage) {
            result.success = false;
            result.message = '该道具只能在战斗中使用';
        }
        else {
            result.success = false;
            result.message = '该道具无法使用';
        }
        
        return result;
    }
    
    /**
     * 检查道具是否可以使用
     * @param {Object} item - 道具对象
     * @param {Object} player - 玩家对象
     * @returns {boolean} 是否可以使用
     */
    static canUseItem(item, player) {
        if (!item || !player) return false;
        
        // 消耗品都可以使用
        if (item.type === 'consumable') {
            // 恢复类道具：检查是否需要恢复
            if (item.healAmount) {
                return player.hp < player.maxHp;
            }
            // 修复类道具：检查是否有战车且需要修复
            if (item.repairAmount) {
                return player.vehicle && 
                       player.vehicle.currentChassisHp < player.vehicle.chassisHp;
            }
            // 其他消耗品
            return true;
        }
        
        return false;
    }
    
    /**
     * 从背包中移除道具
     * @param {Object} item - 道具对象
     * @param {Array} inventory - 背包数组
     * @returns {boolean} 是否移除成功
     */
    static removeFromInventory(item, inventory) {
        return InventoryHelper.removeFromInventory(item, inventory);
    }
    
    /**
     * 获取道具使用描述
     * @param {Object} item - 道具对象
     * @returns {string} 使用描述
     */
    static getUseDescription(item) {
        if (!item) return '';
        
        if (item.type === 'consumable') {
            if (item.healAmount) {
                return `恢复 ${item.healAmount} 点生命值`;
            }
            if (item.repairAmount) {
                return `修复战车 ${item.repairAmount} 点耐久`;
            }
            if (item.fuelAmount) {
                return `补充 ${item.fuelAmount} 点燃料`;
            }
            if (item.damage) {
                return `造成 ${item.damage} 点伤害（仅战斗中使用）`;
            }
        }
        
        return item.description || '无描述';
    }
}

